import React, { Component } from 'react';
import {
    Box, Typography, Button, Table, TableHead, TableBody, TableRow, TableCell,
    TableContainer, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Alert, CircularProgress, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { withAlert } from 'react-alert';

class PrintTokensTab extends Component {
    constructor(props) {
        super(props);

        this.state = {
            tokens: [],
            loading: true,
            mintModalOpen: false,
            clientName: '',
            notes: '',
            minting: false,
            revealModalOpen: false,
            newToken: null,
            copied: false,
            revokeModalOpen: false,
            revokeTarget: null,
            revoking: false,
        };

        this.loadTokens = this.loadTokens.bind(this);
        this.toggleMintModal = this.toggleMintModal.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.mintToken = this.mintToken.bind(this);
        this.copyToken = this.copyToken.bind(this);
        this.closeRevealModal = this.closeRevealModal.bind(this);
        this.openRevokeConfirm = this.openRevokeConfirm.bind(this);
        this.closeRevokeConfirm = this.closeRevokeConfirm.bind(this);
        this.revokeToken = this.revokeToken.bind(this);
    }

    componentDidMount() {
        this.loadTokens();
    }

    loadTokens() {
        this.setState({ loading: true });
        fetch(window.location.protocol + '//' + window.location.host + '/api/printing/tokens')
            .then(res => res.json())
            .then(tokens => this.setState({ tokens, loading: false }))
            .catch(err => {
                console.error(err);
                this.props.alert.error('Failed to load tokens');
                this.setState({ loading: false });
            });
    }

    toggleMintModal() {
        this.setState(prev => ({
            mintModalOpen: !prev.mintModalOpen,
            clientName: '',
            notes: '',
        }));
    }

    handleInputChange(e) {
        const { name, value } = e.target;
        this.setState({ [name]: value });
    }

    mintToken() {
        const { clientName, notes } = this.state;
        if (!clientName.trim()) {
            this.props.alert.error('Client name is required');
            return;
        }

        this.setState({ minting: true });
        fetch(window.location.protocol + '//' + window.location.host + '/api/printing/tokens', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                clientName: clientName.trim(),
                notes: notes.trim() || null
            })
        })
        .then(res => {
            if (!res.ok) throw new Error('Server error');
            return res.json();
        })
        .then(result => {
            this.setState({ minting: false, mintModalOpen: false, revealModalOpen: true, newToken: result, copied: false });
            this.loadTokens();
        })
        .catch(err => {
            console.error(err);
            this.props.alert.error('Failed to add token — is printing:authSecret configured?');
            this.setState({ minting: false });
        });
    }

    copyToken() {
        const { newToken } = this.state;
        if (!newToken) return;
        navigator.clipboard.writeText(newToken.token)
            .then(() => this.setState({ copied: true }))
            .catch(() => {
                const el = document.getElementById('token-reveal-text');
                if (el) { el.select(); document.execCommand('copy'); }
                this.setState({ copied: true });
            });
    }

    closeRevealModal() {
        this.setState({ revealModalOpen: false, newToken: null, copied: false });
    }

    openRevokeConfirm(token) {
        this.setState({ revokeModalOpen: true, revokeTarget: token });
    }

    closeRevokeConfirm() {
        this.setState({ revokeModalOpen: false, revokeTarget: null });
    }

    revokeToken() {
        const { revokeTarget } = this.state;
        if (!revokeTarget) return;

        this.setState({ revoking: true });
        fetch(window.location.protocol + '//' + window.location.host + '/api/printing/tokens/' + revokeTarget.jti, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' }
        })
        .then(res => {
            if (!res.ok) throw new Error('Server error');
            return res.json();
        })
        .then(() => {
            this.props.alert.success('Token revoked');
            this.setState({ revoking: false, revokeModalOpen: false, revokeTarget: null });
            this.loadTokens();
        })
        .catch(err => {
            console.error(err);
            this.props.alert.error('Failed to revoke token');
            this.setState({ revoking: false });
        });
    }

    formatDate(val) {
        if (!val) return '—';
        return new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }

    render() {
        const {
            tokens, loading,
            mintModalOpen, clientName, notes, minting,
            revealModalOpen, newToken, copied,
            revokeModalOpen, revokeTarget, revoking,
        } = this.state;

        return (
            <Box sx={{ textAlign: 'left' }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
                    Websocket Tokens
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Create a Web Socket token to be used to authenticate access to Datel Fusion Inscribe from the print client or Scanning Application. Tokens can be revoked at any time.
                </Typography>

                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee', borderRadius: '12px' }}>
                    <Table size="small">
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500, color: '#555' }}>Client Name</TableCell>
                                <TableCell sx={{ fontWeight: 500, color: '#555' }}>Issued</TableCell>
                                <TableCell sx={{ fontWeight: 500, color: '#555' }}>Notes</TableCell>
                                <TableCell sx={{ fontWeight: 500, color: '#555' }}>Status</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : tokens.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                        No tokens found. Add one to get started.
                                    </TableCell>
                                </TableRow>
                            ) : tokens.map(t => (
                                <TableRow key={t.jti} sx={{ opacity: t.revoked ? 0.5 : 1 }}>
                                    <TableCell>{t.clientName}</TableCell>
                                    <TableCell>{this.formatDate(t.issuedAt)}</TableCell>
                                    <TableCell>{t.notes || '—'}</TableCell>
                                    <TableCell>
                                        {t.revoked
                                            ? <Chip label={`Revoked ${this.formatDate(t.revokedAt)}`} size="small" color="error" variant="outlined" />
                                            : <Chip label="Active" size="small" color="success" variant="outlined" />
                                        }
                                    </TableCell>
                                    <TableCell align="right">
                                        {!t.revoked && (
                                            <Button size="small" color="primary" variant="outlined" onClick={() => this.openRevokeConfirm(t)}>
                                                Revoke
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                    <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={this.toggleMintModal}>
                        Add Token
                    </Button>
                    <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={this.loadTokens}>
                        Refresh
                    </Button>
                </Box>

                {/* Add Token Modal */}
                <Dialog open={mintModalOpen} onClose={this.toggleMintModal} maxWidth="sm" fullWidth>
                    <DialogTitle>Add Websocket Token</DialogTitle>
                    <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            fullWidth size="small" label="Client Name" name="clientName"
                            value={clientName} onChange={this.handleInputChange}
                            placeholder="e.g. WAREHOUSE-PC" required
                            helperText="Must match the Name key in the print agent's App.config."
                        />
                        <TextField
                            fullWidth size="small" label="Notes" name="notes"
                            value={notes} onChange={this.handleInputChange}
                            placeholder="e.g. Office printer"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.toggleMintModal} disabled={minting}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={this.mintToken} disabled={minting}>
                            {minting ? <><CircularProgress size={16} sx={{ mr: 1 }} />Adding...</> : 'Add Token'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Token Reveal Modal */}
                <Dialog open={revealModalOpen} onClose={this.closeRevealModal} maxWidth="md" fullWidth>
                    <DialogTitle>Token Created — Copy Now</DialogTitle>
                    <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Alert severity="warning">
                            <strong>This token will not be shown again.</strong> Copy it now and paste it into the print agent's <code>App.config</code> under the <code>AuthToken</code> key.
                        </Alert>
                        {newToken && (
                            <>
                                <TextField fullWidth size="small" label="Client Name" value={newToken.clientName} InputProps={{ readOnly: true }} />
                                <TextField
                                    fullWidth size="small" label="Token" id="token-reveal-text"
                                    value={newToken.token} InputProps={{ readOnly: true }}
                                    multiline rows={4}
                                    inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' } }}
                                    onClick={e => e.target.select()}
                                />
                                <Button variant="contained" color="primary" onClick={this.copyToken}>
                                    {copied ? '✓ Copied!' : 'Copy to Clipboard'}
                                </Button>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeRevealModal}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Revoke Confirm Modal */}
                <Dialog open={revokeModalOpen} onClose={this.closeRevokeConfirm} maxWidth="sm" fullWidth>
                    <DialogTitle>Revoke Token</DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        {revokeTarget && (
                            <Typography>
                                Are you sure you want to revoke the token for <strong>{revokeTarget.clientName}</strong>?
                                The print agent will be disconnected and unable to reconnect until a new token is issued.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeRevokeConfirm} disabled={revoking}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={this.revokeToken} disabled={revoking}>
                            {revoking ? <><CircularProgress size={16} sx={{ mr: 1 }} />Revoking...</> : 'Revoke'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }
}

export default withAlert()(PrintTokensTab);
